<template>
  <div class="kdoc">
    <node-renderer
      v-for="(n, i) in normalizedDoc"
      :key="i"
      :node="n"
    />
  </div>
</template>

<script>

function ensureArray(x) {
  return Array.isArray(x) ? x : []
}
function ensureRelData(rel) {
  return rel && (rel.data || (rel.value && rel.value.data) || rel) || null
}
function renderTextLeaf(h, leaf) {
  let el = h('span', [leaf.text || ''])
  if (leaf.code) el = h('code', [el])
  if (leaf.bold) el = h('strong', [el])
  if (leaf.italic) el = h('em', [el])
  if (leaf.underline) el = h('span', { style: { textDecoration: 'underline' } }, [el])
  if (leaf.strikethrough) el = h('span', { style: { textDecoration: 'line-through' } }, [el])
  return el
}
function renderInline(h, children) {
  const out = []
  ensureArray(children).forEach(ch => {
    if (typeof ch.text === 'string') {
      out.push(renderTextLeaf(h, ch))
      return
    }
    if (ch && ch.type === 'link') {
      const url = ch.href || ch.url
      out.push(h(
        'a',
        { attrs: { href: url, target: '_blank', rel: 'noopener noreferrer' } },
        ensureArray(ch.children).map(l => renderTextLeaf(h, l))
      ))
      return
    }
    if (ch && Array.isArray(ch.children)) {
      out.push(h('span', ch.children.map(l => renderTextLeaf(h, l))))
    }
  })
  return out
}

const ImageBlock = {
  name: 'ImageBlock',
  functional: true,
  props: { value: { type: Object, required: true } },
  render(h, ctx) {
    const p = ctx.props.value || {}
    const data = ensureRelData(p.asset)
    const file = (data && (data.file || data.imageFile)) || null
    const url = file && file.url || null

    const justify =
      p.align === 'left' ? 'flex-start' :
      p.align === 'right' ? 'flex-end' : 'center'

    return h('div', { style: { display: 'flex', justifyContent: justify } }, [
      h('figure', {
        style: {
          maxWidth: p.maxWidth != null ? `${p.maxWidth}px` : 960,
          width: p.align === 'full' ? '100%' : 'auto',
          margin: 0
        }
      }, [
        url
          ? h('img', {
              attrs: { src: url },
              style: {
                maxWidth: p.maxWidth != null ? `${p.maxWidth}px` : '100%',
                width: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: 0
              }
            })
          : h('div', {
              style: {
                maxWidth: p.maxWidth != null ? `${p.maxWidth}px` : '100%',
                width: '100%',
                paddingTop: '56.25%',
                background: '#eee',
                borderRadius: '8px'
              }
            })
      ])
    ])
  }
}

const GalleryBlock = {
  name: 'GalleryBlock',
  functional: true,
  props: { value: { type: Object, required: true } },
  render(h, ctx) {
    const p = ctx.props.value || {}
    const items = ensureArray(p.items).map(it => {
      const data = ensureRelData(it.asset)
      const file = data && (data.file || data.imageFile)
      return {
        url: file && file.url || null
      }
    })

    const isGrid = p.layout === 'grid'
    const gap = p.gap != null ? p.gap : 12
    const maxWidth = p.maxWidth != null ? p.maxWidth : 1200

    const containerData = isGrid
      ? {
          style: {
            maxWidth: maxWidth + 'px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.max(1, p.columns || 3)}, minmax(0, 1fr))`,
            gridGap: gap + 'px'
          }
        }
      : {
          style: {
            maxWidth: maxWidth + 'px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'stretch',
            overflowX: 'auto',
            gap: gap + 'px'
          }
        }

    const children = items.map((it, i) => {
      const img = it.url
        ? h('img', {
            attrs: { src: it.url },
            style: {
              maxWidth: p.maxWidth != null ? `${p.maxWidth}px` : '100%',
              width: '100%',
              height: 'auto',
              display: 'block',
              borderRadius: 0
            }
          })
        : h('div', {
            style: {

              width: '100%',
              paddingTop: '56.25%',
              background: '#eee',
              borderRadius: '8px'
            }
          })

      const figStyle = isGrid
        ? { margin: 0 }
        : { margin: 0, minWidth: '280px', maxWidth: '420px', marginRight: gap + 'px' }

      return h('figure', { key: i, style: figStyle }, [
        img
      ])
    })

    return h('div', containerData, children)
  }
}

const NodeRenderer = {
  name: 'NodeRenderer',
  functional: true,
  props: { node: { type: Object, required: true } },
  render(h, ctx) {
    const n = ctx.props.node || {}
    const renderChildren = (children) =>
      ensureArray(children).map((c, i) => h(NodeRenderer, { key: i, props: { node: c } }))

    if (typeof n.text === 'string') {
      return renderTextLeaf(h, n)
    }

    switch (n.type) {
      case 'paragraph':
        return h('p', { 
          style: { textAlign: n.textAlign || 'left' }
        }, renderInline(h, n.children))
      case 'heading': {
        const lvl = Math.min(Math.max(n.level || 1, 1), 6)
        return h('h' + lvl, { 
          style: { textAlign: n.textAlign || 'left' }
        }, renderInline(h, n.children))
      }
      case 'blockquote':
        return h('blockquote', { 
          style: { textAlign: n.textAlign || 'left' }
        }, renderChildren(n.children))
      case 'code': {
        const code = ensureArray(n.children).map(c => c.text || '').join('')
        return h('pre', [h('code', [code])])
      }
      case 'divider':
        return h('hr')
      case 'list': {
        const tag = n.listType === 'ordered' ? 'ol' : 'ul'
        return h(tag, { 
          style: { textAlign: n.textAlign || 'left' }
        }, renderChildren(n.children))
      }
      case 'unordered-list':
        return h('ul', { 
          style: { textAlign: n.textAlign || 'left' }
        }, renderChildren(n.children))
      case 'ordered-list':
        return h('ol', { 
          style: { textAlign: n.textAlign || 'left' }
        }, renderChildren(n.children))
      case 'list-item':
        return h('li', { 
          style: { textAlign: n.textAlign || 'left' }
        }, renderChildren(n.children))
      case 'list-item-content':
        return h('div', renderInline(h, n.children))
      case 'link': {
        const url = n.href || n.url
        return h('a', { attrs: { href: url, target: '_blank', rel: 'noopener noreferrer' } },
          renderInline(h, n.children)
        )
      }
      case 'layout': {
        const template = ensureArray(n.layout).map(x => `minmax(0, ${x}fr)`).join(' ')
        return h('div', {
          style: {
            display: 'grid',
            gridTemplateColumns: template || '1fr',
            gridGap: '16px'
          }
        }, renderChildren(n.children))
      }
      case 'component-block': {
        const comp = (n.component || '').toLowerCase()
        if (comp === 'imageblock') return h(ImageBlock, { props: { value: n.props || {} } })
        if (comp === 'galleryblock') return h(GalleryBlock, { props: { value: n.props || {} } })
        return h('pre', { style: { background: '#f6f8fa', padding: '12px' } }, [JSON.stringify(n, null, 2)])
      }
      default:
        return h('div', { 
          style: { textAlign: n.textAlign || 'left' }
        }, renderChildren(n.children))
    }
  }
}

export default {
  name: 'KeystoneDocumentRenderer',
  props: {
    document: { type: Array, required: true }
  },
  components: { NodeRenderer },
  computed: {
    normalizedDoc() {
      return Array.isArray(this.document) ? this.document : []
    }
  }
}
</script>

<style scoped>
.kdoc {
  color: #00202F;
}
.kdoc :where(p, li) { 
  line-height: 1.6; 
  color: #00202F;
  font-weight: 300;
}
.kdoc h1, .kdoc h2, .kdoc h3, .kdoc h4, .kdoc h5, .kdoc h6 {
  line-height: 1.25; 
  margin: 1em 0 .5em;
  color: #00202F;
}
.kdoc p, .kdoc ul, .kdoc ol, .kdoc blockquote, .kdoc pre { 
  margin: .75em 0; 
  color: #00202F;
}
.kdoc blockquote { 
  border-left: 4px solid #e5e7eb; 
  padding-left: 12px; 
  color: #00202F; 
}
.kdoc pre { 
  background: #0b1021; 
  color: #f6f6f6; 
  padding: 12px; 
  border-radius: 8px; 
  overflow-x: auto; 
}
.kdoc a { 
  text-decoration: underline; 
  color: #00202F;
}
.kdoc span, .kdoc strong, .kdoc em, .kdoc code {
  color: #00202F;
}
</style>



// WEBPACK FOOTER //
// KeystoneDocumentRenderer.vue?347a60e2